import { browserLogger } from '@newfront-insurance/dd-rum';
import { AuthOptions } from '@newfront-insurance/ms-graph-auth';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { NovaSettingsData, NovaSettingField } from '../../../hooks/use-nova-settings';
import { useMSGraphAuth } from '../contexts/ms-graph-context';

export interface OutlookConnectController {
  // State
  outlookConnectEnabled: boolean;

  /** True when Outlook is enabled but auth has expired and interactive login is required. */
  outlookNeedsReauth: boolean;

  // Actions
  setOutlookConnectEnabled(enabled: boolean): void;

  /**
   * Attempt interactive login if Outlook is enabled but auth has expired.
   * Must be called from a user gesture (click handler) so the popup is not blocked.
   * Returns true if a login popup was triggered.
   */
  reconnectIfNeeded(): Promise<boolean>;
}

export function useOutlookConnectController({
  msGraphEnabled,
  isChatVisible,
  notify,
  serverSettings,
  onPersist,
}: {
  msGraphEnabled: boolean;
  isChatVisible: boolean;
  notify: { success: (msg: string) => void; error: (msg: string) => void };
  serverSettings?: NovaSettingsData | null;
  onPersist?: (field: NovaSettingField, value: unknown) => void;
}): OutlookConnectController {
  const msGraphAuth = useMSGraphAuth();

  // Initialize from server settings if available, default to false
  const [outlookConnectEnabled, setOutlookConnectEnabledState] = useState(
    () => serverSettings?.outlookConnectEnabled ?? false,
  );
  const [outlookNeedsReauth, setOutlookNeedsReauth] = useState(false);

  // Sync from server settings when they arrive
  useEffect(() => {
    if (serverSettings != null) {
      setOutlookConnectEnabledState(serverSettings.outlookConnectEnabled);
    }
  }, [serverSettings]);

  // Feature flag kill switch - automatically disconnect when flag is OFF
  useEffect(() => {
    if (!msGraphEnabled && msGraphAuth?.authState.state === AuthOptions.LOGGED_IN) {
      setOutlookConnectEnabledState(false);
      onPersist?.('outlookConnectEnabled', false);
    }
  }, [msGraphEnabled, msGraphAuth, onPersist]);

  // Proactive re-authentication: When Nova window is opened and toggle is ON but user is logged out, trigger login
  // This ensures toggle state always matches auth state when user actively opens Nova (toggle ON = authenticated)
  const authStateValue = msGraphAuth?.authState.state;

  // Track whether we've already attempted silent reconnect this session.
  // Prevents infinite loops: loginSilent fails → state becomes INTERACTION_REQUIRED →
  // effect re-fires → loginSilent again → loop.
  const silentReconnectAttemptedRef = useRef(false);

  // Reset the flag when the user explicitly logs in successfully (e.g. via toggle)
  // so that future disconnects can attempt silent reconnect again.
  useEffect(() => {
    if (authStateValue === AuthOptions.LOGGED_IN) {
      silentReconnectAttemptedRef.current = false;
    }
  }, [authStateValue]);

  useEffect(() => {
    if (
      isChatVisible &&
      outlookConnectEnabled &&
      msGraphAuth &&
      !silentReconnectAttemptedRef.current &&
      (authStateValue === AuthOptions.LOGGED_OUT || authStateValue === AuthOptions.INTERACTION_REQUIRED)
    ) {
      silentReconnectAttemptedRef.current = true;

      browserLogger.info(
        '[NovaProvider] Nova opened with toggle ON but user logged out - attempting silent re-authentication',
        { authState: authStateValue },
      );
      // Silent-only — never open a popup automatically. Popups should only be
      // triggered by the user explicitly toggling Outlook on. With cacheLocation
      // set to localStorage, silent auth succeeds in steady state. If it fails
      // (e.g. first load after cache migration), the user can re-toggle.
      msGraphAuth
        .loginSilent()
        .then((authState) => {
          browserLogger.info('[NovaProvider] Silent re-authentication completed', {
            newAuthState: authState.state,
            wasSuccessful: authState.state === AuthOptions.LOGGED_IN,
          });
          if (authState.state === AuthOptions.LOGGED_IN) {
            notify.success('Outlook connected successfully');
            setOutlookNeedsReauth(false);
          } else {
            setOutlookNeedsReauth(true);
          }
        })
        .catch((error) => {
          browserLogger.error('[NovaProvider] Silent re-authentication failed', error);
          setOutlookNeedsReauth(true);
        });
    }
  }, [isChatVisible, outlookConnectEnabled, authStateValue, msGraphAuth, notify]);

  // Disable Outlook and reset state
  const disableOutlook = useCallback(() => {
    setOutlookConnectEnabledState(false);
    onPersist?.('outlookConnectEnabled', false);

    // Log out of MS Graph so toggling back ON requires re-authentication
    if (msGraphAuth) {
      msGraphAuth.logout().catch((error) => {
        browserLogger.error('[OutlookConnect] Failed to logout from MS Graph', { error });
      });
    }
  }, [onPersist, msGraphAuth]);

  // Handle authentication cancellation
  const handleAuthCancelled = useCallback(() => {
    disableOutlook();
    notify.error('Outlook authentication was cancelled. Please enable again in Settings.');
  }, [disableOutlook, notify]);

  // Handle authentication failure
  const handleAuthError = useCallback(
    (error: Error) => {
      disableOutlook();
      notify.error(`Outlook authentication failed: ${error.message || 'Please try again.'}`);
    },
    [disableOutlook, notify],
  );

  // Enable Outlook and trigger authentication
  const enableOutlook = useCallback(async () => {
    setOutlookConnectEnabledState(true);
    onPersist?.('outlookConnectEnabled', true);

    if (!msGraphAuth) {
      return;
    }

    const isLoggedIn = msGraphAuth.authState.state === AuthOptions.LOGGED_IN;
    if (isLoggedIn) {
      // Already logged in - no toast needed, user just toggled ON
      return;
    }

    try {
      const authState = await msGraphAuth.login();
      if (authState.state === AuthOptions.LOGGED_IN) {
        // Show toast only after successful authentication
        notify.success('Outlook connected successfully');
      } else {
        handleAuthCancelled();
      }
    } catch (error) {
      handleAuthError(error as Error);
    }
  }, [msGraphAuth, handleAuthCancelled, handleAuthError, notify, onPersist]);

  // Handle Outlook Connect toggle changes
  const setOutlookConnectEnabled = useCallback(
    (enabled: boolean) => {
      if (enabled) {
        enableOutlook();
      } else {
        disableOutlook();
      }
    },
    [enableOutlook, disableOutlook],
  );

  // Attempt interactive login if Outlook is enabled but auth has expired.
  // Called from click handlers (user gesture) so the popup is allowed by the browser.
  const reconnectIfNeeded = useCallback(async (): Promise<boolean> => {
    if (!outlookConnectEnabled || !msGraphAuth) return false;

    const needsLogin =
      msGraphAuth.authState.state === AuthOptions.LOGGED_OUT ||
      msGraphAuth.authState.state === AuthOptions.INTERACTION_REQUIRED;

    if (!needsLogin) return false;

    try {
      const authState = await msGraphAuth.login();
      if (authState.state === AuthOptions.LOGGED_IN) {
        setOutlookNeedsReauth(false);
        notify.success('Outlook connected successfully');
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }, [outlookConnectEnabled, msGraphAuth, notify]);

  return useMemo(
    () => ({
      outlookConnectEnabled,
      outlookNeedsReauth,
      setOutlookConnectEnabled,
      reconnectIfNeeded,
    }),
    [outlookConnectEnabled, outlookNeedsReauth, setOutlookConnectEnabled, reconnectIfNeeded],
  );
}
